import { IonCol, IonGrid, IonList, IonRow, IonText } from '@ionic/react';
import { useRef } from 'react';

import styles from '@/components/Clients/ModalContent.module.css';
import { Input } from '@/components/Input';
import { CustomLoading } from '@/components/Loading/CustomLoading';

import { mergeClassNames } from '@/utils/mergeClassNames';

import { useHideKeyboardOnEnter } from '@/hooks/useHideKeyboardOnEnter';
import { useScrollToInputWhenKeyboardOpens } from '@/hooks/useScrollToInputWhenKeyboardOpens';

type ClientModalContentProps = {
  mode: 'create' | 'edit';
  clientData: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    email: string;
  };
  errors: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  onInputChange: (field: keyof ClientModalContentProps['clientData'], value: string) => void;
  loading: boolean;
};

export const ClientModalContent = ({ mode, clientData, errors, onInputChange, loading }: ClientModalContentProps) => {
  const emailRef = useRef<HTMLIonInputElement>(null);
  const firstNameRef = useRef<HTMLIonInputElement>(null);
  const lastNameRef = useRef<HTMLIonInputElement>(null);

  useHideKeyboardOnEnter(firstNameRef);
  useHideKeyboardOnEnter(lastNameRef);
  useHideKeyboardOnEnter(emailRef);

  useScrollToInputWhenKeyboardOpens();

  const loadingMessage = mode === 'create' ? 'Creating Client...' : 'Updating Client...';

  return (
    <div className={styles.container}>
      {loading && (
        <div className={styles.loadingOverlay}>
          <CustomLoading message={loadingMessage} size='large' />
        </div>
      )}

      <IonGrid className={styles.wrapper}>
        <IonRow
          className={mergeClassNames('ion-align-items-start', styles['no-padding'], styles.inputRow)}
          style={{ width: '100%' }}>
          <IonCol className={styles.nameCol} size='6'>
            <IonList class={mergeClassNames(styles['no-padding'], styles.list)} mode='md'>
              <Input
                disabled={loading}
                enterkeyhint='done'
                hasError={!!errors.firstName}
                label='Name'
                labelPlacement='stacked'
                onIonInput={(e) => onInputChange('firstName', e.detail.value!)}
                parentRef={firstNameRef}
                placeholder='John'
                required
                type='text'
                value={clientData.firstName}
                variant='light'
              />
              {errors.firstName && (
                <IonText className={styles.errorText} color='danger'>
                  {errors.firstName}
                </IonText>
              )}
            </IonList>
          </IonCol>

          <IonCol className={styles.surnameCol} size='6'>
            <IonList class={mergeClassNames(styles['no-padding'], styles.list)} mode='md'>
              <Input
                disabled={loading}
                enterkeyhint='done'
                hasError={!!errors.lastName}
                label='Surname'
                labelPlacement='stacked'
                onIonInput={(e) => onInputChange('lastName', e.detail.value!)}
                parentRef={lastNameRef}
                placeholder='Doe'
                required
                type='text'
                value={clientData.lastName}
                variant='light'
              />
              {errors.lastName && (
                <IonText className={styles.errorText} color='danger'>
                  {errors.lastName}
                </IonText>
              )}
            </IonList>
          </IonCol>
        </IonRow>

        <IonRow
          className={mergeClassNames('ion-align-items-center ion-justify-content-center', styles['no-padding'])}
          style={{ width: '100%' }}>
          <IonCol className={styles['no-padding']} size='12'>
            <IonList class={mergeClassNames(styles['no-padding'], styles.list)} mode='md'>
              {mode === 'create' && (
                <Input
                  className={styles.inputRow}
                  disabled={loading}
                  label='Date of birth'
                  labelPlacement='stacked'
                  onIonInput={(e) => onInputChange('dateOfBirth', e.detail.value!)}
                  placeholder={'00/00/0000'}
                  type='date'
                  value={clientData.dateOfBirth}
                  variant='light'
                />
              )}

              {/* Email field */}
              <Input
                disabled={loading}
                enterkeyhint='done'
                hasError={!!errors.email}
                label='Email address'
                labelPlacement='stacked'
                onIonInput={(e) => onInputChange('email', e.detail.value!)}
                parentRef={emailRef}
                placeholder='name@mail.com'
                required
                type='email'
                value={clientData.email}
                variant='light'
              />

              {errors.email && (
                <IonText className={styles.errorText} color='danger'>
                  {errors.email}
                </IonText>
              )}
            </IonList>
          </IonCol>
        </IonRow>
      </IonGrid>
    </div>
  );
};
